/**
 * AddonVolumePricingEditor — Per-addon quantity-based pricing
 *
 * Only shown when a "Free Addon" perk is defined.
 * Each row: addon item, min_qty, max_qty, rate.
 *
 * Part of Plan 199, Epic 02.
 */

import { useCallback } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface AddonVolumeRow {
  localId: string;
  addon_item: string;
  min_qty: string;
  max_qty: string;
  rate: string;
}

interface Props {
  tiers: AddonVolumeRow[];
  onChange: (tiers: AddonVolumeRow[]) => void;
}

export function AddonVolumePricingEditor({ tiers, onChange }: Props) {
  const updateRow = useCallback(
    (index: number, field: keyof AddonVolumeRow, value: string) => {
      const next = [...tiers];
      next[index] = { ...next[index], [field]: value };
      onChange(next);
    },
    [tiers, onChange]
  );

  const addRow = useCallback(() => {
    onChange([
      ...tiers,
      {
        localId: crypto.randomUUID(),
        addon_item: "",
        min_qty: "1",
        max_qty: "",
        rate: "",
      },
    ]);
  }, [tiers, onChange]);

  const removeRow = useCallback(
    (index: number) => {
      onChange(tiers.filter((_, i) => i !== index));
    },
    [tiers, onChange]
  );

  return (
    <div className="space-y-3">
      {tiers.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No addon volume pricing defined. Add tiers to set per-addon
          quantity-based pricing.
        </p>
      )}

      {/* Header */}
      {tiers.length > 0 && (
        <div className="grid grid-cols-[1fr_80px_80px_100px_36px] gap-2 px-1">
          <span className="text-xs font-medium text-muted-foreground">
            Addon Item
          </span>
          <span className="text-xs font-medium text-muted-foreground">
            Min Qty
          </span>
          <span className="text-xs font-medium text-muted-foreground">
            Max Qty
          </span>
          <span className="text-xs font-medium text-muted-foreground">
            Rate
          </span>
          <span />
        </div>
      )}

      {tiers.map((tier, index) => (
        <div
          key={tier.localId}
          className="grid grid-cols-[1fr_80px_80px_100px_36px] gap-2 items-start"
        >
          <Input
            value={tier.addon_item}
            onChange={(e) => updateRow(index, "addon_item", e.target.value)}
            placeholder="Item code"
            className="h-9 text-sm"
          />
          <Input
            type="number"
            min={1}
            value={tier.min_qty}
            onChange={(e) => updateRow(index, "min_qty", e.target.value)}
            className="h-9 text-sm"
            placeholder="1"
          />
          <Input
            type="number"
            min={1}
            value={tier.max_qty}
            onChange={(e) => updateRow(index, "max_qty", e.target.value)}
            className="h-9 text-sm"
            placeholder="∞"
          />
          <Input
            type="number"
            min={0}
            step="0.01"
            value={tier.rate}
            onChange={(e) => updateRow(index, "rate", e.target.value)}
            className="h-9 text-sm"
            placeholder="0.00"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-red-500"
            onClick={() => removeRow(index)}
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}

      <Button
        variant="ghost"
        size="sm"
        className="text-xs text-muted-foreground"
        onClick={addRow}
      >
        <Plus className="w-3.5 h-3.5 mr-1" />
        Add Addon Tier
      </Button>
    </div>
  );
}
